import React from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, radius, shadows, spacing, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

interface Props {
  visible: boolean;
  onEnable: () => void;
  onNotNow: () => void;
}

export function LocationPermissionModal({ visible, onEnable, onNotNow }: Props) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors } = useAppTheme();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onNotNow}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onNotNow} />
        
        <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]}>
          {/* Header Icon Ring */}
          <View style={styles.iconContainer}>
            <View style={styles.iconOuterRing}>
              <View style={[styles.iconInnerRing, { backgroundColor: colors.surface }]}>
                <Ionicons name="location-sharp" size={32} color={colors.accent} />
              </View>
            </View>
          </View>

          {/* Title & Description */}
          <Text style={[styles.title, { color: colors.textPrimary }]}>{trText('Use your location?', 'আপনার লোকেশন ব্যবহার করবেন?')}</Text>
          <Text style={[styles.description, { color: colors.textSecondary }]}>
            {trText('SalunBD can show salons and stylists near you, sorted by distance for easy booking.', 'SalunBD আপনার কাছের সেলুন ও স্টাইলিস্ট দূরত্ব অনুযায়ী সাজিয়ে দেখাতে পারে, যাতে সহজে বুক করতে পারেন।')}
          </Text>

          {/* Benefits Box */}
          <View style={[styles.benefitsBox, { backgroundColor: colors.surfaceMuted, borderColor: colors.border }]}>
            <View style={styles.benefitRow}>
              <View style={styles.benefitIconWrap}>
                <Ionicons name="checkmark-circle" size={18} color={colors.accent} />
              </View>
              <Text style={[styles.benefitText, { color: colors.textPrimary }]}>{trText('Find top-rated shops in your neighborhood', 'আপনার এলাকার সেরা রেটিংয়ের শপ সার্চ করুন')}</Text>
            </View>

            <View style={styles.benefitRow}>
              <View style={styles.benefitIconWrap}>
                <Ionicons name="checkmark-circle" size={18} color={colors.accent} />
              </View>
              <Text style={[styles.benefitText, { color: colors.textPrimary }]}>{trText('Accurate distances and real arrival times', 'সঠিক দূরত্ব ও পৌঁছানোর আসল সময়')}</Text>
            </View>
          </View>

          {/* Actions */}
          <View style={styles.actions}>
            <Pressable
              accessibilityRole="button"
              onPress={onEnable}
              style={({ pressed }) => [
                styles.primaryBtn,
                pressed && styles.btnPressed,
              ]}>
              <Ionicons name="navigate" size={18} color="#FFFFFF" style={styles.btnIcon} />
              <Text style={styles.primaryBtnText}>{trText('Enable Location', 'লোকেশন চালু করুন')}</Text>
            </Pressable>

            <Pressable
              accessibilityRole="button"
              onPress={onNotNow}
              style={({ pressed }) => [
                styles.secondaryBtn,
                pressed && { backgroundColor: colors.surfaceMuted },
              ]}>
              <Text style={[styles.secondaryBtnText, { color: colors.textSecondary }]}>{trText('Not Now', 'এখন না')}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = ScaledSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: colors.surface,
    borderRadius: 24,
    paddingHorizontal: spacing.xl,
    paddingTop: spacing.xxl,
    paddingBottom: spacing.xl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    ...Platform.select({
      android: { elevation: 12 },
      web: { boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.25)' },
      default: {
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 16 },
        shadowOpacity: 0.2,
        shadowRadius: 28,
      },
    }),
  },
  iconContainer: {
    marginBottom: spacing.md,
  },
  iconOuterRing: {
    width: 76,
    height: 76,
    borderRadius: 38,
    backgroundColor: colors.accentSoft,
    borderWidth: 1.5,
    borderColor: 'rgba(99, 91, 255, 0.25)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconInnerRing: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: colors.surface,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadows.card,
  },
  title: {
    fontFamily: fontFamily.extraBold,
    fontSize: 22,
    lineHeight: 28,
    color: colors.textPrimary,
    textAlign: 'center',
    letterSpacing: -0.3,
  },
  description: {
    fontFamily: fontFamily.regular,
    fontSize: 14.5,
    lineHeight: 21,
    color: colors.textSecondary,
    textAlign: 'center',
    marginTop: spacing.xs,
    marginBottom: spacing.lg,
    paddingHorizontal: spacing.xs,
  },
  benefitsBox: {
    width: '100%',
    backgroundColor: colors.surfaceMuted,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    gap: spacing.sm,
    marginBottom: spacing.xl,
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  benefitIconWrap: {
    width: 20,
    alignItems: 'center',
  },
  benefitText: {
    flex: 1,
    fontFamily: fontFamily.medium,
    fontSize: 13,
    lineHeight: 18,
    color: colors.textPrimary,
  },
  actions: {
    width: '100%',
    gap: spacing.sm,
  },
  primaryBtn: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.accentFill, // Brand Purple #635BFF
    paddingVertical: 14,
    borderRadius: radius.md,
    gap: 8,
    ...Platform.select({
      android: { elevation: 4 },
      web: { boxShadow: `0px 6px 16px ${colors.accent}40` },
      default: {
        shadowColor: colors.accent,
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.3,
        shadowRadius: 12,
      },
    }),
  },
  btnIcon: {
    marginRight: 2,
  },
  primaryBtnText: {
    fontFamily: fontFamily.bold,
    fontSize: 15.5,
    color: colors.white,
    letterSpacing: 0.1,
  },
  btnPressed: {
    opacity: 0.88,
    transform: [{ scale: 0.99 }],
  },
  secondaryBtn: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: radius.md,
  },
  secondaryBtnPressed: {
    backgroundColor: colors.surfaceMuted,
  },
  secondaryBtnText: {
    fontFamily: fontFamily.semiBold,
    fontSize: 14.5,
    color: colors.textSecondary,
  },
});
